import { useAuth } from "@/_core/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import { hasCapability } from "@shared/finance/capabilities";
import type { FinanceRole } from "@shared/finance/capabilities";
import type { SupplierContact } from "@shared/finance/suppliers";
import { Building2, Download, Edit2, FileUp, Plus, RotateCcw, Search, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import { SupplierCreateModal } from "./SupplierCreateModal";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "../../../../server/routers";

type SupplierImportPreview = inferRouterOutputs<AppRouter>["financeSuppliers"]["previewBulkImport"];

export function SuppliersTab() {
  const { user } = useAuth();
  const canWrite = user ? hasCapability(user.role as FinanceRole, "finance.write") : false;

  const [search, setSearch] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [supplierToDeactivate, setSupplierToDeactivate] = useState<any | null>(null);
  const [duplicateCif, setDuplicateCif] = useState<{ taxId: string; name: string } | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<{ name: string; base64: string } | null>(null);
  const [importPreview, setImportPreview] = useState<SupplierImportPreview | null>(null);
  const [importOverrides, setImportOverrides] = useState<Record<number, Partial<Pick<SupplierImportPreview["rows"][number], "name" | "taxId" | "contactPerson" | "contactRole" | "email" | "phone">>>>({});
  const [importChangesNeedReview, setImportChangesNeedReview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editForm, setEditForm] = useState({
    name: "",
    taxId: "",
    contactName: "",
    contacts: [{ person: "", role: "", email: "", phone: "" }] as SupplierContact[],
    notes: "",
  });

  const query = trpc.financeSuppliers.list.useQuery({ active: null });
  const templateQuery = trpc.financeSuppliers.downloadImportTemplate.useQuery(undefined, { enabled: false });

  const previewImportMutation = trpc.financeSuppliers.previewBulkImport.useMutation({
    onSuccess: (result) => { setImportPreview(result); setImportChangesNeedReview(false); },
    onError: (error) => toast.error(error.message),
  });
  const commitImportMutation = trpc.financeSuppliers.commitBulkImport.useMutation({
    onSuccess: (result) => {
      toast.success(`Importación completada: ${result.created} creados, ${result.alreadyExists} ya existentes.`);
      closeImportDialog(false);
      query.refetch();
    },
    onError: (error) => toast.error(error.message),
  });

  const updateMutation = trpc.financeSuppliers.update.useMutation({
    onSuccess: () => {
      query.refetch();
      setEditingId(null);
    },
    onError: (err) => {
      const duplicate = err.message.match(/SUPPLIER_CIF_ALREADY_REGISTERED:([^|]+)\|(.+)/);
      if (duplicate) setDuplicateCif({ taxId: duplicate[1], name: duplicate[2] });
      else toast.error(err.message);
    },
  });

  const toggleActiveMutation = trpc.financeSuppliers.setActive.useMutation({
    onSuccess: () => {
      query.refetch();
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const filteredSuppliers = (query.data ?? []).filter((supplier: any) => {
    const term = search.toLowerCase();
    return (
      supplier.name?.toLowerCase().includes(term) ||
      supplier.taxId?.toLowerCase().includes(term)
    );
  });

  function handleCreate(supplier: { id: number; name: string; taxId?: string | null }) {
    query.refetch();
    setCreateModalOpen(false);
  }

  function startEdit(supplier: any) {
    setEditingId(supplier.id);
    setEditForm({
      name: supplier.name || "",
      taxId: supplier.taxId || "",
      contactName: supplier.contactName || "",
      contacts: supplier.contacts?.length ? supplier.contacts.map((contact: SupplierContact) => ({ ...contact })) : [{ person: supplier.contactPerson || "", role: supplier.contactRole || "", email: supplier.email || "", phone: supplier.phone || "" }],
      notes: supplier.notes || "",
    });
  }

  function saveEdit(id: number) {
    updateMutation.mutate({
      id,
      name: editForm.name.trim(),
      taxId: editForm.taxId.trim() || null,
      contactName: editForm.contactName.trim() || undefined,
      contacts: editForm.contacts.map((contact) => ({ person: contact.person?.trim() || null, role: contact.role?.trim() || null, email: contact.email?.trim() || null, phone: contact.phone?.trim() || null })).filter((contact) => Object.values(contact).some(Boolean)),
      notes: editForm.notes.trim() || null,
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setEditForm({
      name: "",
      taxId: "",
      contactName: "",
      contacts: [{ person: "", role: "", email: "", phone: "" }],
      notes: "",
    });
  }

  function toggleActive(id: number, active: boolean) {
    toggleActiveMutation.mutate({ id, active: !active });
  }

  function updateContact(index: number, field: keyof SupplierContact, value: string) {
    setEditForm((previous) => ({ ...previous, contacts: previous.contacts.map((contact, current) => current === index ? { ...contact, [field]: value } : contact) }));
  }

  async function downloadImportTemplate() {
    try {
      const { data } = await templateQuery.refetch();
      if (!data) throw new Error("No se pudo generar la plantilla.");
      const bytes = Uint8Array.from(atob(data.base64), (character) => character.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = data.fileName;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No se pudo descargar la plantilla.");
    }
  }

  async function readImportFile(file?: File) {
    setImportPreview(null);
    setImportOverrides({});
    setImportChangesNeedReview(false);
    if (!file) { setImportFile(null); return; }
    if (!file.name.toLowerCase().endsWith(".xlsx")) { setImportFile(null); toast.error("Selecciona un archivo Excel .xlsx."); return; }
    if (file.size > 6_000_000) { setImportFile(null); toast.error("El archivo no puede superar 6 MB."); return; }
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => typeof reader.result === "string" ? resolve(reader.result) : reject(new Error("No se pudo leer el archivo."));
        reader.onerror = () => reject(new Error("No se pudo leer el archivo."));
        reader.readAsDataURL(file);
      });
      setImportFile({ name: file.name, base64: dataUrl.slice(dataUrl.indexOf(",") + 1) });
    } catch (error) {
      setImportFile(null);
      toast.error(error instanceof Error ? error.message : "No se pudo leer el archivo.");
    }
  }

  function closeImportDialog(open: boolean) {
    setImportDialogOpen(open);
    if (!open) {
      setImportFile(null);
      setImportPreview(null);
      setImportOverrides({});
      setImportChangesNeedReview(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  function editImportRow(rowNumber: number, field: keyof typeof importOverrides[number], value: string) {
    setImportChangesNeedReview(true);
    setImportOverrides((previous) => ({
      ...previous,
      [rowNumber]: { ...previous[rowNumber], [field]: value },
    }));
    setImportPreview((previous) => previous ? ({
      ...previous,
      rows: previous.rows.map((row) => row.rowNumber === rowNumber ? { ...row, [field]: value } : row),
    }) : previous);
  }

  function reviewImportChanges() {
    if (!importFile) return;
    previewImportMutation.mutate({
      base64: importFile.base64,
      overrides: Object.entries(importOverrides).map(([rowNumber, values]) => ({ rowNumber: Number(rowNumber), ...values })),
    });
  }

  const emptyState = (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed bg-muted/20 px-4 py-10 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-background text-muted-foreground shadow-sm">
        <Building2 className="h-5 w-5" />
      </div>
      <div className="max-w-md space-y-1">
        <p className="text-sm font-medium text-foreground">Sin proveedores registrados</p>
        <p className="text-sm text-muted-foreground">Crea el primero para empezar a gestionar tus proveedores.</p>
      </div>
      {canWrite && (
        <Button onClick={() => setCreateModalOpen(true)}>
          <Plus className="h-4 w-4 mr-1.5" />
          Crear primer proveedor
        </Button>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">Proveedores</h2>
          <p className="text-sm text-muted-foreground">Gestión de proveedores</p>
          <p className="text-xs text-muted-foreground">Inactivar conserva el histórico y no desactiva al cliente. Los cambios de NIF requieren revisión de identidad.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={downloadImportTemplate} disabled={templateQuery.isFetching}>
            <Download className="h-4 w-4 mr-1.5" />Descargar plantilla Excel
          </Button>
          {canWrite && <>
            <Button variant="outline" onClick={() => setImportDialogOpen(true)}><FileUp className="h-4 w-4 mr-1.5" />Importar Excel</Button>
            <Button onClick={() => setCreateModalOpen(true)}><Plus className="h-4 w-4 mr-1.5" />Nuevo proveedor</Button>
          </>}
        </div>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre o CIF/NIF..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {query.isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Cargando proveedores...</div>
      ) : query.isError ? (
        <div className="text-center py-12 text-destructive">
          Error al cargar proveedores: {query.error.message}
        </div>
      ) : filteredSuppliers.length === 0 ? (
        emptyState
      ) : (
        <div className="rounded-lg border overflow-x-auto">
          <Table className="min-w-[900px]">
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>CIF/NIF</TableHead>
                <TableHead>Contactos</TableHead>
                <TableHead>Nombre comercial</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSuppliers.map((supplier: any) => (
                <TableRow key={supplier.id}>
                  <TableCell className="font-medium">
                    {editingId === supplier.id ? (
                      <Input
                        value={editForm.name}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                        className="h-8"
                      />
                    ) : (
                      supplier.name
                    )}
                  </TableCell>
                  <TableCell>
                    {editingId === supplier.id ? (
                      <Input
                        value={editForm.taxId}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, taxId: e.target.value }))}
                        className="h-8"
                      />
                    ) : (
                      supplier.taxId || "-"
                    )}
                  </TableCell>
                  <TableCell className="min-w-[320px]">
                    {editingId === supplier.id ? (
                      <div className="space-y-2">
                        {editForm.contacts.map((contact, index) => <div key={index} className="space-y-1.5 rounded border p-2">
                          <div className="flex items-center justify-between"><span className="text-xs font-medium">Contacto {index + 1}</span>{index > 0 && <Button type="button" size="sm" variant="ghost" aria-label={`Eliminar contacto ${index + 1}`} onClick={() => setEditForm((previous) => ({ ...previous, contacts: previous.contacts.filter((_, current) => current !== index) }))}><Trash2 className="h-4 w-4" /></Button>}</div>
                          <Input aria-label={`Persona de contacto ${index + 1}`} placeholder="Persona de contacto" value={contact.person || ""} onChange={(event) => updateContact(index, "person", event.target.value)} className="h-8" />
                          <Input aria-label={`Cargo ${index + 1}`} placeholder="Cargo o departamento" value={contact.role || ""} onChange={(event) => updateContact(index, "role", event.target.value)} className="h-8" />
                          <Input aria-label={`Email ${index + 1}`} placeholder="Email" value={contact.email || ""} onChange={(event) => updateContact(index, "email", event.target.value)} className="h-8" />
                          <Input aria-label={`Teléfono ${index + 1}`} placeholder="Teléfono" value={contact.phone || ""} onChange={(event) => updateContact(index, "phone", event.target.value)} className="h-8" />
                        </div>)}
                        <Button type="button" size="sm" variant="outline" onClick={() => setEditForm((previous) => ({ ...previous, contacts: [...previous.contacts, { person: "", role: "", email: "", phone: "" }] }))}><Plus className="mr-1 h-4 w-4" />Añadir otro contacto</Button>
                      </div>
                    ) : supplier.contacts?.length ? <div className="space-y-2">{supplier.contacts.map((contact: SupplierContact, index: number) => <div key={`${supplier.id}-contact-${index}`} className="space-y-0.5"><div className="font-medium">{contact.person || "Sin nombre"}{contact.role ? ` · ${contact.role}` : ""}</div>{contact.email && <div className="text-xs text-muted-foreground">{contact.email}</div>}{contact.phone && <div className="text-xs text-muted-foreground">{contact.phone}</div>}</div>)}</div> : "-"}
                  </TableCell>
                  <TableCell>
                    {editingId === supplier.id ? (
                      <Input
                        value={editForm.contactName}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, contactName: e.target.value }))}
                        className="h-8"
                      />
                    ) : (
                      supplier.contactName || "-"
                    )}
                  </TableCell>
                  <TableCell>
                    {editingId === supplier.id ? (
                      <Input
                        value={editForm.notes}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, notes: e.target.value }))}
                        className="h-8"
                      />
                    ) : (
                      <Badge variant={supplier.active ? "default" : "secondary"}>
                        {supplier.active ? "Activo" : "Inactivo"}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {canWrite && (
                      <div className="flex items-center justify-end gap-2">
                        {editingId === supplier.id ? (
                          <>
                            <Button size="sm" variant="ghost" disabled={updateMutation.isPending} onClick={() => saveEdit(supplier.id)}>
                              Guardar
                            </Button>
                            <Button size="sm" variant="ghost" onClick={cancelEdit}>
                              Cancelar
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button size="sm" variant="ghost" aria-label={`Editar proveedor ${supplier.name}`} onClick={() => startEdit(supplier)}>
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            {supplier.active ? (
                              <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" aria-label={`Inactivar proveedor ${supplier.name}`} title="Inactivar proveedor (conserva su histórico)" disabled={toggleActiveMutation.isPending} onClick={() => setSupplierToDeactivate(supplier)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button size="sm" variant="ghost" aria-label={`Reactivar proveedor ${supplier.name}`} title="Reactivar proveedor" disabled={toggleActiveMutation.isPending} onClick={() => toggleActive(supplier.id, supplier.active)}>
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <SupplierCreateModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onCreated={handleCreate}
        preventExistingSupplier
      />

      <Dialog open={Boolean(supplierToDeactivate)} onOpenChange={(open) => { if (!open) setSupplierToDeactivate(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Inactivar proveedor</DialogTitle>
            <DialogDescription>
              ¿Quieres inactivar a <strong>{supplierToDeactivate?.name}</strong>? No se borrarán sus facturas ni su histórico y podrá reactivarse más adelante.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSupplierToDeactivate(null)}>Cancelar</Button>
            <Button variant="destructive" disabled={toggleActiveMutation.isPending} onClick={() => {
              if (supplierToDeactivate) toggleActiveMutation.mutate({ id: supplierToDeactivate.id, active: false }, { onSuccess: () => setSupplierToDeactivate(null) });
            }}>Inactivar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(duplicateCif)} onOpenChange={(open) => { if (!open) setDuplicateCif(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>El CIF/NIF ya está registrado</DialogTitle>
            <DialogDescription>
              El CIF/NIF <strong>{duplicateCif?.taxId}</strong> ya está dado de alta para <strong>{duplicateCif?.name}</strong>. Revisa los datos antes de guardar; no se ha creado ningún duplicado.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter><Button onClick={() => setDuplicateCif(null)}>Entendido</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importDialogOpen} onOpenChange={closeImportDialog}>
        <DialogContent className="flex h-[92vh] w-[calc(100vw-2rem)] max-w-none flex-col overflow-hidden p-4 sm:max-w-none sm:p-6">
          <DialogHeader>
            <DialogTitle>Importar proveedores desde Excel</DialogTitle>
            <DialogDescription>Selecciona la plantilla rellenada. Primero revisaremos las filas; no se guardará nada hasta que confirmes.</DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overflow-x-hidden pr-1">
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" onClick={downloadImportTemplate} disabled={templateQuery.isFetching}><Download className="mr-2 h-4 w-4" />Descargar plantilla</Button>
              <input ref={fileInputRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" disabled={previewImportMutation.isPending || commitImportMutation.isPending} onChange={(event) => readImportFile(event.target.files?.[0])} className="block w-full min-w-0 max-w-full text-sm" />
            </div>
            <p className="text-xs text-muted-foreground">Hoja “Proveedores”; nombre y CIF/NIF son obligatorios. Máximo 500 filas y 6 MB. Los proveedores ya existentes no se modifican.</p>
            {importFile && <div className="text-sm">Archivo seleccionado: <span className="font-medium">{importFile.name}</span></div>}
            {importPreview && <>
              <div className="grid gap-2 text-sm sm:grid-cols-3">
                <div className="rounded-md border p-3">Nuevos: <strong>{importPreview.createCount}</strong></div>
                <div className="rounded-md border p-3">Ya existentes: <strong>{importPreview.existingCount}</strong></div>
                <div className="rounded-md border p-3">Requieren revisión: <strong>{importPreview.blockedCount}</strong></div>
              </div>
              {importPreview.duplicateRows > 0 && <Alert><AlertDescription>Se ignorarán {importPreview.duplicateRows} fila(s) repetidas idénticas dentro del Excel.</AlertDescription></Alert>}
              {importPreview.blockedCount > 0 && <Alert variant="destructive"><AlertDescription>Las filas que requieren revisión se pueden corregir aquí mismo. Edita sus datos y pulsa «Revisar cambios»; el Excel seleccionado se conserva.</AlertDescription></Alert>}
              <div className="space-y-2">
                <div className="hidden grid-cols-[3rem_minmax(10rem,1.2fr)_minmax(9rem,1fr)_minmax(9rem,1fr)_minmax(9rem,1fr)_minmax(9rem,1fr)_minmax(9rem,1fr)_minmax(12rem,1.5fr)] gap-2 px-3 text-xs font-medium text-muted-foreground xl:grid">
                  <span>Fila</span><span>Proveedor</span><span>CIF/NIF</span><span>Persona de contacto</span><span>Cargo</span><span>Email</span><span>Teléfono</span><span>Resultado</span>
                </div>
                <div className="max-h-[48vh] space-y-2 overflow-y-auto overflow-x-hidden pr-1">
                  {importPreview.rows.map((row) => {
                    const editable = row.status === "review_required";
                    const fieldClass = "min-w-0 w-full";
                    return <div key={`${row.rowNumber}-${row.taxId}`} className="grid min-w-0 grid-cols-1 gap-2 rounded-md border p-3 md:grid-cols-2 xl:grid-cols-[3rem_minmax(10rem,1.2fr)_minmax(9rem,1fr)_minmax(9rem,1fr)_minmax(9rem,1fr)_minmax(9rem,1fr)_minmax(9rem,1fr)_minmax(12rem,1.5fr)] xl:items-center">
                      <span className="text-xs text-muted-foreground xl:text-sm">Fila {row.rowNumber}</span>
                      {editable ? <Input aria-label={`Proveedor fila ${row.rowNumber}`} className={fieldClass} value={row.name} onChange={(event) => editImportRow(row.rowNumber, "name", event.target.value)} /> : <span className="min-w-0 break-words text-sm">{row.name}</span>}
                      {editable ? <Input aria-label={`CIF/NIF fila ${row.rowNumber}`} className={fieldClass} value={row.taxId} onChange={(event) => editImportRow(row.rowNumber, "taxId", event.target.value)} /> : <span className="min-w-0 break-words text-sm">{row.taxId}</span>}
                      {editable ? <Input aria-label={`Contacto fila ${row.rowNumber}`} className={fieldClass} value={row.contactPerson ?? ""} onChange={(event) => editImportRow(row.rowNumber, "contactPerson", event.target.value)} placeholder="Persona de contacto" /> : <span className="min-w-0 break-words text-sm">{row.contactPerson || "—"}</span>}
                      {editable ? <Input aria-label={`Cargo fila ${row.rowNumber}`} className={fieldClass} value={row.contactRole ?? ""} onChange={(event) => editImportRow(row.rowNumber, "contactRole", event.target.value)} placeholder="Cargo" /> : <span className="min-w-0 break-words text-sm">{row.contactRole || "—"}</span>}
                      {editable ? <Input aria-label={`Email fila ${row.rowNumber}`} className={fieldClass} value={row.email ?? ""} onChange={(event) => editImportRow(row.rowNumber, "email", event.target.value)} placeholder="Email" /> : <span className="min-w-0 break-words text-sm">{row.email || "—"}</span>}
                      {editable ? <Input aria-label={`Teléfono fila ${row.rowNumber}`} className={fieldClass} value={row.phone ?? ""} onChange={(event) => editImportRow(row.rowNumber, "phone", event.target.value)} placeholder="Teléfono" /> : <span className="min-w-0 break-words text-sm">{row.phone || "—"}</span>}
                      <div className="min-w-0 break-words text-sm"><div>{row.status === "new" ? "Se creará" : row.status === "already_exists" ? "Ya existe; sin cambios" : "Revisión necesaria"}</div>{row.status === "review_required" && <div className="text-xs text-destructive">{row.message}</div>}</div>
                    </div>;
                  })}
                </div>
              </div>
            </>}
          </div>
          <DialogFooter className="shrink-0 flex-wrap border-t pt-3">
            <Button variant="outline" onClick={() => closeImportDialog(false)}>Cancelar</Button>
            <Button variant="outline" disabled={!importFile || previewImportMutation.isPending} onClick={() => importFile && (Object.keys(importOverrides).length ? reviewImportChanges() : previewImportMutation.mutate({ base64: importFile.base64 }))}>{previewImportMutation.isPending ? "Revisando..." : Object.keys(importOverrides).length ? "Revisar cambios" : "Revisar archivo"}</Button>
            <Button disabled={!importFile || !importPreview || importPreview.createCount === 0 || importPreview.blockedCount > 0 || importChangesNeedReview || commitImportMutation.isPending} onClick={() => importFile && commitImportMutation.mutate({ base64: importFile.base64, overrides: Object.entries(importOverrides).map(([rowNumber, values]) => ({ rowNumber: Number(rowNumber), ...values })) })}>{commitImportMutation.isPending ? "Guardando..." : "Confirmar importación"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

